import { BACKEND_URL } from "../env";
import { ApiError } from "../types/api-error";
import {
  Invoice,
  Plan,
  SeatInfo,
  SelectPlanResult,
  Subscription,
  UpgradePreview,
} from "../types/entities";

export async function fetchSeatInfo(
  clientId: string
): Promise<SeatInfo | null> {
  if (!clientId) return null;
  try {
    const response = await fetch(
      `${BACKEND_URL}/subscriptions/seat-info?clientId=${encodeURIComponent(clientId)}`,
      { credentials: "include", cache: "no-store" }
    );
    if (response.status >= 400) return null;
    return (await response.json()) as SeatInfo;
  } catch {
    return null;
  }
}

export async function fetchPlans(): Promise<Plan[]> {
  try {
    const response = await fetch(`${BACKEND_URL}/subscriptions/plans`, {
      credentials: "include",
      cache: "no-store",
    });
    if (response.status >= 400) return [];
    return (await response.json()) as Plan[];
  } catch {
    return [];
  }
}

export async function fetchSubscription(
  clientId: string
): Promise<Subscription | null> {
  if (!clientId) return null;
  try {
    const response = await fetch(
      `${BACKEND_URL}/subscriptions/me?clientId=${encodeURIComponent(clientId)}`,
      { credentials: "include", cache: "no-store" }
    );
    if (response.status >= 400) return null;
    const data = await response.json();
    if (data && "subscription" in data && data.subscription === null) {
      return null;
    }
    return data as Subscription;
  } catch {
    return null;
  }
}

/**
 * Invoice history (`GET /subscriptions/invoices`, #2791/#2794), shown by
 * `InvoiceHistoryList` on the Pricing/Billing tab. Owner-or-Admin
 * read-only on the backend (#2794's UX answer superseding the earlier
 * Owner-only decision) — an Admin gets the same list as the Owner, never a
 * 403, so this helper does not need to branch on role itself. Follows the
 * same "an error and an empty list look the same" convention as
 * `fetchPlans`: a failure and "no invoices yet" are indistinguishable to
 * the caller, which is fine here since `InvoiceHistoryList` already shows
 * an identical empty state for both.
 */
export async function fetchInvoices(clientId: string): Promise<Invoice[]> {
  if (!clientId) return [];
  try {
    const response = await fetch(
      `${BACKEND_URL}/subscriptions/invoices?clientId=${encodeURIComponent(clientId)}`,
      { credentials: "include", cache: "no-store" }
    );
    if (response.status >= 400) return [];
    return (await response.json()) as Invoice[];
  } catch {
    return [];
  }
}

/**
 * First-time plan selection (`POST /subscriptions`, #2792). Unlike
 * `requestPlanChange` (`PATCH`, below), this no longer returns a plain
 * `Subscription` — the local subscription is created in `incomplete`
 * status, and the backend also returns a Stripe PaymentIntent
 * `clientSecret` so the frontend can mount Stripe Elements and confirm
 * payment client-side (see `SelectPlanResponseDto` on the backend and
 * `stripe-checkout-form.tsx`). Card data never touches this function or
 * any other application code — only Stripe's own SDK sees it.
 */
export async function selectPlan(
  clientId: string,
  planKey: string
): Promise<{
  success: boolean;
  subscription?: Subscription;
  clientSecret?: string;
  error?: ApiError;
}> {
  try {
    const response = await fetch(`${BACKEND_URL}/subscriptions`, {
      method: "POST",
      credentials: "include",
      cache: "no-store",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ clientId, planKey }),
    });
    if (response.status >= 400) {
      const errorData = await response.json().catch(() => ({}));
      const code =
        errorData?.code ??
        (response.status === 409
          ? "conflict"
          : response.status === 404
            ? "not_found"
            : undefined);
      const message = errorData?.message || "Failed to select plan.";
      return {
        success: false,
        error: { status: response.status, code, message },
      };
    }
    const result = (await response.json()) as SelectPlanResult;
    return {
      success: true,
      subscription: result.subscription,
      clientSecret: result.clientSecret,
    };
  } catch {
    return {
      success: false,
      error: { message: "Network error. Please try again." },
    };
  }
}

/**
 * Previews the exact prorated charge for upgrading `clientId`'s
 * subscription to `planKey`, without applying anything
 * (`GET /subscriptions/upgrade-preview`, #2787). Per the UX/UI design spec
 * on #2785 ("show the exact charge ... do not hallucinate"), callers must
 * only ever render `preview.amountDueEur` as returned here — never compute
 * or estimate a proration amount themselves. A failed/unavailable preview
 * is reported via `success: false` rather than thrown, matching every
 * other call in this file, so the caller can fall back to generic
 * "calculated automatically by Stripe" copy instead of guessing a number.
 */
export async function previewUpgrade(
  clientId: string,
  planKey: string
): Promise<{
  success: boolean;
  preview?: UpgradePreview;
  error?: ApiError;
}> {
  try {
    const response = await fetch(
      `${BACKEND_URL}/subscriptions/upgrade-preview?clientId=${encodeURIComponent(
        clientId
      )}&planKey=${encodeURIComponent(planKey)}`,
      { credentials: "include", cache: "no-store" }
    );
    if (response.status >= 400) {
      const errorData = await response.json().catch(() => ({}));
      const code = errorData?.errorCode ?? errorData?.code;
      const message =
        errorData?.message || "Failed to calculate the prorated charge.";
      return {
        success: false,
        error: { status: response.status, code, message },
      };
    }
    const preview = (await response.json()) as UpgradePreview;
    return { success: true, preview };
  } catch {
    return {
      success: false,
      error: { message: "Network error. Please try again." },
    };
  }
}

export async function requestPlanChange(
  clientId: string,
  planKey: string
): Promise<{
  success: boolean;
  subscription?: Subscription;
  error?: ApiError;
}> {
  return await postPlan(`${BACKEND_URL}/subscriptions`, "PATCH", {
    clientId,
    planKey,
  });
}

async function postPlan(
  url: string,
  method: "PATCH",
  body: { clientId: string; planKey: string }
): Promise<{
  success: boolean;
  subscription?: Subscription;
  error?: ApiError;
}> {
  try {
    const response = await fetch(url, {
      method,
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (response.status >= 400) {
      const errorData = await response.json().catch(() => ({}));
      // `errorCode` is the machine-readable code set for deliberate
      // rejections such as `DOWNGRADE_BLOCKED` (see
      // `subscription-errors.constants.ts` on the backend) — checked
      // before the generic HTTP-status-derived `code` fallback so the
      // caller (`ChangePlanModal`) can always branch on it rather than
      // pattern-matching `message`.
      const code =
        errorData?.errorCode ??
        errorData?.code ??
        (response.status === 409
          ? "conflict"
          : response.status === 404
            ? "not_found"
            : undefined);
      const message = errorData?.message || "Failed to update plan.";
      return {
        success: false,
        error: { status: response.status, code, message },
      };
    }
    const subscription = (await response.json()) as Subscription;
    return { success: true, subscription };
  } catch {
    return {
      success: false,
      error: { message: "Network error. Please try again." },
    };
  }
}

/**
 * Schedules cancellation at the end of the current billing period
 * (`POST /subscriptions/cancel`, #2786). Never cancels immediately, and
 * there is no self-service "undo" once this succeeds — the Owner stays on
 * their current plan (`subscription.status === 'cancel_scheduled'`) until
 * the Stripe-confirmed `cancelAt` date, at which point the webhook handler
 * falls the client back to the free tier.
 */
export async function cancelSubscription(clientId: string): Promise<{
  success: boolean;
  subscription?: Subscription;
  error?: ApiError;
}> {
  try {
    const response = await fetch(`${BACKEND_URL}/subscriptions/cancel`, {
      method: "POST",
      credentials: "include",
      cache: "no-store",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ clientId }),
    });
    if (response.status >= 400) {
      const errorData = await response.json().catch(() => ({}));
      const code =
        errorData?.errorCode ??
        errorData?.code ??
        (response.status === 404 ? "not_found" : undefined);
      const message = errorData?.message || "Failed to cancel the plan.";
      return {
        success: false,
        error: { status: response.status, code, message },
      };
    }
    const subscription = (await response.json()) as Subscription;
    return { success: true, subscription };
  } catch {
    return {
      success: false,
      error: { message: "Network error. Please try again." },
    };
  }
}

/**
 * Starts a Stripe SetupIntent so the Owner can replace their stored card
 * (`POST /subscriptions/update-payment-method`, #2795) — the "past due"
 * banner's update-payment-method dialog. Mirrors `selectPlan`'s
 * `clientSecret` shape, but no charge happens here (see the backend's
 * `StripeService.createSetupIntent`); card data never touches this
 * function or any other application code.
 */
export async function createPaymentMethodUpdateSetupIntent(
  clientId: string
): Promise<{
  success: boolean;
  clientSecret?: string;
  error?: ApiError;
}> {
  try {
    const response = await fetch(
      `${BACKEND_URL}/subscriptions/update-payment-method`,
      {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientId }),
      }
    );
    if (response.status >= 400) {
      const errorData = await response.json().catch(() => ({}));
      const code =
        errorData?.errorCode ??
        errorData?.code ??
        (response.status === 404 ? "not_found" : undefined);
      const message =
        errorData?.message || "Failed to start the payment method update.";
      return {
        success: false,
        error: { status: response.status, code, message },
      };
    }
    const result = (await response.json()) as { clientSecret: string };
    return { success: true, clientSecret: result.clientSecret };
  } catch {
    return {
      success: false,
      error: { message: "Network error. Please try again." },
    };
  }
}

/**
 * Confirms a payment-method update once the frontend has confirmed the
 * SetupIntent with Stripe Elements (`POST
 * /subscriptions/update-payment-method/confirm`, #2795): sets the new
 * payment method as Stripe's default for future invoice retries. Does NOT
 * force an immediate retry of the failed invoice — per the #2785 Story 4
 * lenient/graceful policy, the subscription only returns to `active` once
 * Stripe's own webhook confirms `invoice.paid` (see
 * `home-portal-plugin.ts`'s bounded poll after this call succeeds).
 */
export async function confirmPaymentMethodUpdate(
  clientId: string,
  paymentMethodId: string
): Promise<{
  success: boolean;
  subscription?: Subscription | null;
  error?: ApiError;
}> {
  try {
    const response = await fetch(
      `${BACKEND_URL}/subscriptions/update-payment-method/confirm`,
      {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientId, paymentMethodId }),
      }
    );
    if (response.status >= 400) {
      const errorData = await response.json().catch(() => ({}));
      const code =
        errorData?.errorCode ??
        errorData?.code ??
        (response.status === 404 ? "not_found" : undefined);
      const message =
        errorData?.message || "Failed to confirm the payment method update.";
      return {
        success: false,
        error: { status: response.status, code, message },
      };
    }
    const subscription = (await response.json()) as Subscription | null;
    return { success: true, subscription };
  } catch {
    return {
      success: false,
      error: { message: "Network error. Please try again." },
    };
  }
}
